import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import Api from "../../Api/api";
import { toast } from "react-toastify";
import { useStateValue } from "../../states/StateProvider";
import { AES, enc } from "crypto-js";

const UpdateUpselling = ({ data, toggle, options, FPID, dataFull, editID }) => {
    const [loading, setLoading] = useState(false);
    const [{ upsellingSaved, secretKey }, dispatch] = useStateValue();

    const [showOptions, setShowOptions] = useState(false);
    const [optionsUpselling, setOptionsUpselling] = useState([]);
    const [selectedOption, setSelectedOption] = useState(
        options?.find((op) => op.title === data)
    );

    // dropdown positioning
    const triggerRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({});

    const positionDropdown = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const padding = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // available space
        const belowSpace = vh - rect.bottom - padding;
        const aboveSpace = rect.top - padding;

        // pick side with more space (prefer below)
        const useBelow = belowSpace >= 180 || belowSpace >= aboveSpace;

        const left = Math.min(rect.left, vw - rect.width - padding);
        const width = rect.width;

        const maxHeight = useBelow ? belowSpace : aboveSpace;
        const top = useBelow ? rect.bottom + 4 : Math.max(padding, rect.top - Math.min(300, maxHeight) - 4);

        setMenuStyle({
            position: "fixed",
            top,
            left,
            width,
            maxHeight: Math.max(140, maxHeight), // keep it usable
            overflowY: "auto",
            zIndex: 60, // above modal (z-50)
        });
    }, []);

    useEffect(() => {
        const ups = [
            { title: "ja", ID: 1 },
            { title: "nein", ID: 2 },
            { title: "unbekannt", ID: 3 },
        ];
        setOptionsUpselling(ups);
    }, []);

    const { register, handleSubmit, setValue, formState: { isValid } } = useForm({ mode: "onChange" });

    useEffect(() => { register("status", { required: true }); }, [register]);
    useEffect(() => { setValue("status", data, { shouldValidate: true }); }, [data, setValue]);

    useEffect(() => {
        const onDoc = () => setShowOptions(false);
        if (showOptions) {
            positionDropdown();
            window.addEventListener("resize", positionDropdown);
            window.addEventListener("scroll", positionDropdown, true);
            document.addEventListener("mousedown", onDoc);
        }
        return () => {
            window.removeEventListener("resize", positionDropdown);
            window.removeEventListener("scroll", positionDropdown, true);
            document.removeEventListener("mousedown", onDoc);
        };
    }, [showOptions, positionDropdown]);

    const handleOptionClick = (option) => {
        if (option.title === "Vertrag aktiv" || option.title === "Vertrag inaktiv") return;
        setSelectedOption(option);
        setValue("status", option.title, { shouldValidate: true, shouldDirty: true });
        setShowOptions(false);
    };

    const decryptedBytes = localStorage.getItem("user")
        ? AES.decrypt(localStorage.getItem("user"), secretKey)
        : false;
    // const user = JSON.parse(decryptedBytes.toString(enc.Utf8));

    const onSubmit = async (form) => {
        let OptionID;

        options?.forEach((op) => op.title === form.status && (OptionID = op.ID));
        if (OptionID == null) {
            optionsUpselling?.forEach((op) => op.title === form.status && (OptionID = op.ID));
        }

        setLoading(true);
        const payload = { UID: OptionID, portal: "dgg", ID: editID, FP_ID: FPID, status: form.status };

        Api().post("/sp_setUpselling", payload)
            .then(() => {
                toast.success("Daten erfolgreich aktualisiert");
                setLoading(false);
                dispatch({ type: "SET_UPSELLING_SAVED", item: !upsellingSaved });
                toggle();
            })
            .catch(() => {
                setLoading(false);
                toast.error("Etwas ist schief gelaufen!!");
            });
    };

    const currentTitle = dataFull?.[0] ?? "";
    const currentStatus = dataFull?.[1]?.split(",")?.[0] ?? "";
    const currentDate = dataFull?.[1]?.split(",")?.[1]?.split(":")?.[1] ?? "";

    // Options source depending on section
    const sourceOptions = currentTitle === "Upselling"
        ? optionsUpselling
        : options || [];

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="relative w-full">
            {/* header: title left, status pill right */}
            <div className="mb-6 flex items-center justify-between text-text">
                <h3 className="text-lg font-semibold">{currentTitle}</h3>

                <div className="inline-flex items-center gap-2 rounded-md border border-offWhite bg-gray-50 px-3 py-1 text-sm">
                    {currentStatus !== "Keine Information" && (
                        <img
                            src={`${window.location.origin}/icons/${currentStatus.replace(/\s/g, "")}.png`}
                            alt={currentStatus}
                            className="h-5 w-5"
                        />
                    )}
                    <span className="flex items-center gap-2">
            <span>{currentStatus}</span>
            <span className="text-gray-500">{currentDate}</span>
          </span>
                </div>
            </div>

            {/* field */}
            <label className="mb-1 block text-sm text-gray-600">
                neuer status *
            </label>
            <div className="relative">
                {/* trigger */}
                <div
                    ref={triggerRef}
                    className="w-full cursor-pointer rounded-md border border-whiteDark bg-white px-4 py-2 text-left"
                    onClick={() => setShowOptions((s) => !s)}
                >
                    {selectedOption ? (
                        <div className="flex items-center gap-2">
                            {selectedOption.icon && (
                                <img
                                    src={`${window.location.origin}/icons/${selectedOption.icon}`}
                                    alt={selectedOption.title}
                                    className="h-5 w-5"
                                />
                            )}
                            <span>{selectedOption.title}</span>
                            <span className="ml-auto">{showOptions ? "▲" : "▼"}</span>
                        </div>
                    ) : (
                        "Status wählen"
                    )}
                </div>

                {/* dropdown in a portal so it can overflow the modal freely */}
                {showOptions &&
                    createPortal(
                        <div
                            style={menuStyle}
                            className="rounded-md border border-whiteDark bg-white shadow-md"
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {sourceOptions.map((op) => {
                                const locked = op.title === "Vertrag aktiv" || op.title === "Vertrag inaktiv";
                                return (
                                    <div
                                        key={op.ID}
                                        className={`flex items-center gap-2 px-4 py-2 ${
                                            locked
                                                ? "pointer-events-none cursor-not-allowed text-grey"
                                                : "cursor-pointer text-text hover:bg-gray-50"
                                        }`}
                                        onClick={() => handleOptionClick(op)}
                                    >
                                        {op.icon && (
                                            <img
                                                src={`${window.location.origin}/icons/${op.icon}`}
                                                alt={op.title}
                                                className={`h-5 w-5 ${locked ? "opacity-70" : ""}`}
                                            />
                                        )}
                                        <span className="ml-2">{op.title}</span>
                                    </div>
                                );
                            })}
                        </div>,
                        document.body
                    )}
            </div>

            {/* actions */}
            <div className="mt-6 grid grid-cols-2 gap-3">
                <input
                    type="submit"
                    disabled={!isValid || loading}
                    value={!loading ? "speichern" : "speichere..."}
                    className={`w-full rounded-md text-white ${
                        isValid && !loading ? "cursor-pointer bg-mainBlue hover:bg-mainBlue/90" : "bg-disableBlue"
                    }`}
                />

                <button
                    type="button"
                    onClick={toggle}
                    className="w-full rounded-md bg-whiteDark px-6 text-sm font-semibold text-text hover:bg-text hover:text-white"
                >
                    abbrechen
                </button>
            </div>
        </form>
    );
};

export default UpdateUpselling;
